import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const workflowsRulesConfigurationValueSchema = z.strictObject({
    defaultEnabledOnPublish: z.boolean(),
    maximumRules: z.union([z.literal(100), z.literal(250), z.literal(500), z.literal(1_000)]),
    maximumConditionsPerRule: z.number().int().min(1).max(25),
    maximumActionReferencesPerRule: z.number().int().min(1).max(10),
});
export const workflowsRulesConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'workflows.rules.configuration',
        moduleId: 'workflows',
        title: 'Workflow rule behavior',
        helpText: 'Controls tenant rule limits and lifecycle defaults within the platform hard limits.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            defaultEnabledOnPublish: false,
            maximumRules: 500,
            maximumConditionsPerRule: 25,
            maximumActionReferencesPerRule: 10,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: workflowsRulesConfigurationValueSchema,
});
export const workflowsRulesDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    showDescriptions: z.boolean(),
    showVersionMetadata: z.boolean(),
    editorWidth: z.enum(['standard', 'wide']),
});
export const workflowsRulesDisplaySetting = definePlatformSetting({
    definition: {
        key: 'workflows.rules.display',
        moduleId: 'workflows',
        title: 'Workflow rule display',
        helpText: 'Controls tenant and user display preferences for the rule list and editor.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 50,
            density: 'comfortable',
            showDescriptions: true,
            showVersionMetadata: true,
            editorWidth: 'wide',
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: workflowsRulesDisplayValueSchema,
});
const workflowsRuleStatusLabelsSchema = z.strictObject({
    draft: z.string().trim().min(1).max(60),
    active: z.string().trim().min(1).max(60),
    disabled: z.string().trim().min(1).max(60),
    archived: z.string().trim().min(1).max(60),
});
export const workflowsRulesCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    createAction: z.string().trim().min(1).max(80),
    appendVersionAction: z.string().trim().min(1).max(80),
    previewAction: z.string().trim().min(1).max(80),
    publishAction: z.string().trim().min(1).max(80),
    enableAction: z.string().trim().min(1).max(80),
    disableAction: z.string().trim().min(1).max(80),
    archiveAction: z.string().trim().min(1).max(80),
    rollbackAction: z.string().trim().min(1).max(80),
    nameLabel: z.string().trim().min(1).max(80),
    descriptionLabel: z.string().trim().min(1).max(80),
    triggerLabel: z.string().trim().min(1).max(80),
    conditionsLabel: z.string().trim().min(1).max(80),
    actionsLabel: z.string().trim().min(1).max(80),
    reasonLabel: z.string().trim().min(1).max(80),
    eventPayloadLabel: z.string().trim().min(1).max(80),
    loadingLabel: z.string().trim().min(1).max(120),
    savingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    previewMatchedLabel: z.string().trim().min(1).max(120),
    previewNotMatchedLabel: z.string().trim().min(1).max(120),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    statusLabels: workflowsRuleStatusLabelsSchema,
});
export const workflowsRulesCopySetting = definePlatformSetting({
    definition: {
        key: 'workflows.copy.rules',
        moduleId: 'workflows',
        title: 'Workflow rule copy',
        helpText: 'Controls tenant-editable labels for workflow rules, versioning, and dry-run preview.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Workflows',
            title: 'Workflow rules',
            description: 'Create versioned automation rules and test them safely before activation.',
            createAction: 'Create rule',
            appendVersionAction: 'Save new version',
            previewAction: 'Preview rule',
            publishAction: 'Publish version',
            enableAction: 'Enable rule',
            disableAction: 'Disable rule',
            archiveAction: 'Archive rule',
            rollbackAction: 'Roll back to version',
            nameLabel: 'Rule name',
            descriptionLabel: 'Description',
            triggerLabel: 'Versioned trigger event',
            conditionsLabel: 'Conditions (JSON)',
            actionsLabel: 'Action references (JSON)',
            reasonLabel: 'Change reason',
            eventPayloadLabel: 'Preview event payload (JSON)',
            loadingLabel: 'Loading workflow rules…',
            savingLabel: 'Saving workflow rule…',
            loadError: 'Workflow rules could not be loaded.',
            mutationError: 'The workflow rule change could not be saved. Refresh and try again.',
            previewMatchedLabel: 'The preview event matches this rule.',
            previewNotMatchedLabel: 'The preview event does not match this rule.',
            emptyTitle: 'No workflow rules yet',
            emptyDescription: 'Create a draft rule, test it, then publish and enable it when ready.',
            statusLabels: {
                draft: 'Draft', active: 'Active', disabled: 'Disabled', archived: 'Archived',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: workflowsRulesCopyValueSchema,
});
export const workflowsSettingRegistrations = [
    workflowsRulesConfigurationSetting,
    workflowsRulesDisplaySetting,
    workflowsRulesCopySetting,
];
//# sourceMappingURL=workflows-setting-definitions.js.map